/**
 * File attachment validation utilities for feedback system
 */

// Allowed file types with their MIME types
export const ALLOWED_FILE_TYPES = {
  'image/png': { extension: 'PNG', maxSize: 10 * 1024 * 1024 },
  'image/jpeg': { extension: 'JPG', maxSize: 10 * 1024 * 1024 },
  'image/jpg': { extension: 'JPG', maxSize: 10 * 1024 * 1024 },
  'image/gif': { extension: 'GIF', maxSize: 10 * 1024 * 1024 },
  'application/pdf': { extension: 'PDF', maxSize: 10 * 1024 * 1024 },
  'text/plain': { extension: 'TXT', maxSize: 10 * 1024 * 1024 },
  'text/x-log': { extension: 'LOG', maxSize: 10 * 1024 * 1024 },
  // Additional common text file types
  'application/x-log': { extension: 'LOG', maxSize: 10 * 1024 * 1024 },
};

// File size limits
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
export const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB total
export const MAX_FILES = 5;

/**
 * Validates a single file
 * @param {File} file - The file to validate
 * @returns {Object} Validation result with isValid and error message
 */
export const validateSingleFile = (file) => {
  // Check file type
  if (!ALLOWED_FILE_TYPES[file.type]) {
    const allowedExtensions = Object.values(ALLOWED_FILE_TYPES)
      .map(type => type.extension)
      .join(', ');
    return {
      isValid: false,
      error: `File type not allowed. Allowed types: ${allowedExtensions}`
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File too large. Maximum size: ${formatFileSize(MAX_FILE_SIZE)}`
    };
  }

  // Check for empty files
  if (file.size === 0) {
    return {
      isValid: false,
      error: 'File is empty'
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates multiple files
 * @param {File[]} files - Array of files to validate
 * @returns {Object} Validation result with detailed information
 */
export const validateAttachments = (files) => {
  const result = {
    isValid: true,
    errors: [],
    validFiles: [],
    invalidFiles: [],
    totalSize: 0
  };

  // Check file count
  if (files.length > MAX_FILES) {
    result.isValid = false;
    result.errors.push(`Maximum ${MAX_FILES} files allowed. You selected ${files.length} files.`);
  }

  // Validate each file
  files.forEach((file, index) => {
    const validation = validateSingleFile(file);
    
    if (validation.isValid) {
      result.validFiles.push(file);
      result.totalSize += file.size;
    } else {
      result.isValid = false;
      result.invalidFiles.push({
        file,
        error: validation.error,
        index
      });
    }
  });

  // Check total size
  if (result.totalSize > MAX_TOTAL_SIZE) {
    result.isValid = false;
    result.errors.push(
      `Total file size exceeds limit. Maximum: ${formatFileSize(MAX_TOTAL_SIZE)}, ` +
      `Current: ${formatFileSize(result.totalSize)}`
    );
  }

  return result;
};

/**
 * Formats file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Gets file extension from file name
 * @param {string} fileName - The file name
 * @returns {string} File extension in uppercase
 */
export const getFileExtension = (fileName) => {
  return fileName.split('.').pop()?.toUpperCase() || '';
};

/**
 * Checks if a file type is an image
 * @param {string} mimeType - The MIME type of the file
 * @returns {boolean} True if the file is an image
 */
export const isImageFile = (mimeType) => {
  return mimeType.startsWith('image/');
};

/**
 * Generates a unique file name for storage
 * @param {string} originalName - Original file name
 * @param {string} feedbackId - Feedback submission ID
 * @param {number} index - File index
 * @returns {string} Unique file name for storage
 */
export const generateStorageFileName = (originalName, feedbackId, index) => {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop();
  const baseName = originalName.split('.').slice(0, -1).join('.');
  const sanitizedBaseName = baseName.replace(/[^a-zA-Z0-9-_]/g, '_');
  
  return `feedback/${feedbackId}/${timestamp}_${index}_${sanitizedBaseName}.${extension}`;
};